import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const HERO_BG =
    "https://images.unsplash.com/photo-1581084349663-7ab88c58d362?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHx2aW55bCUyMHJlY29yZCUyMGFic3RyYWN0JTIwZGFya3xlbnwwfHx8fDE3ODY4NjM4NzJ8MA&ixlib=rb-4.1.0&q=85";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
    <span className={`block overflow-hidden ${className}`}>
        <motion.span
            className="block"
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.2, delay, ease: [0.16, 1, 0.3, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

export default function HeroSection({ onNavigate }) {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
    const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

    return (
        <section
            ref={ref}
            data-testid="hero-section"
            className="relative flex min-h-screen flex-col overflow-hidden"
        >
            <motion.div style={{ y: bgY }} className="absolute inset-0">
                <img
                    src={HERO_BG}
                    alt=""
                    className="h-[125%] w-full object-cover"
                />
                <div className="absolute inset-0 bg-black/60" />
                <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-void" />
            </motion.div>
            <div className="absolute left-[10%] top-1/4 h-[55vh] w-[55vw] rounded-full bg-blood/20 blur-[130px]" />

            <motion.header
                style={{ opacity: fade }}
                className="relative z-10 flex items-center justify-between px-6 py-6 md:px-16"
            >
                <span
                    data-testid="nav-wordmark"
                    className="font-serif text-xl italic tracking-tight"
                >
                    bejnni
                </span>
                <nav className="flex gap-8 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400">
                    <button
                        data-testid="nav-listen-link"
                        onClick={() => onNavigate("#listen")}
                        className="transition-colors duration-300 hover:text-bone"
                    >
                        Listen
                    </button>
                    <button
                        data-testid="nav-world-link"
                        onClick={() => onNavigate("#world")}
                        className="transition-colors duration-300 hover:text-bone"
                    >
                        World
                    </button>
                    <button
                        data-testid="nav-social-link"
                        onClick={() => onNavigate("#social")}
                        className="transition-colors duration-300 hover:text-bone"
                    >
                        Social
                    </button>
                </nav>
            </motion.header>

            <motion.div
                style={{ opacity: fade }}
                className="relative z-10 mt-auto px-6 pb-28 md:px-16 md:pb-32"
            >
                <MaskedLine delay={0.2}>
                    <span
                        data-testid="hero-kicker"
                        className="mb-6 inline-block font-mono text-xs tracking-[0.35em] text-zinc-400 md:text-sm"
                    >
                        i bloom better when i'm dead — out now
                    </span>
                </MaskedLine>
                <h1
                    data-testid="hero-title"
                    className="font-serif text-[22vw] leading-[0.85] tracking-tighter md:text-[18vw]"
                >
                    <MaskedLine delay={0.35}>BEJNNI</MaskedLine>
                </h1>
                <div className="mt-8 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
                    <MaskedLine delay={0.6}>
                        <p className="max-w-md font-serif text-xl italic text-zinc-300 md:text-2xl">
                            I turned everything that tried to destroy me into
                            something worth screaming about
                        </p>
                    </MaskedLine>
                    <motion.button
                        data-testid="hero-listen-button"
                        onClick={() => onNavigate("#listen")}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.2, duration: 0.8 }}
                        whileTap={{ scale: 0.95 }}
                        className="group flex w-fit items-center gap-4 border border-white/20 px-8 py-4 font-mono text-xs uppercase tracking-[0.3em] transition-colors duration-500 hover:border-blood hover:bg-blood/10"
                    >
                        Listen now
                        <ArrowDown className="h-4 w-4 transition-transform duration-500 group-hover:translate-y-1" />
                    </motion.button>
                </div>
            </motion.div>
        </section>
    );
}
